import { CreateContractTerminationBody } from "@entity/data/dto/employee/contracts/termination/create-contract-termination.body";
import { MedicalLeavePeriodMapper } from "@entity/data/mappers/employee/contracts/termination/medical-leave-period.mapper";
import { MedicalLeaveSubtypeEnumMapper } from "@entity/data/mappers/employee/medical-leave-subtype-enum.mapper";
import { TerminationCategoryEnumMapper } from "@entity/data/mappers/employee/termination-category-enum.mapper";
import { TerminationReasonEnumMapper } from "@entity/data/mappers/employee/termination-reason-enum.mapper";
import { CreateContractTermination } from "@entity/domain/models/employee/contracts/termination/create-contract-termination.model";
import { CreateMedicalLeavePeriod } from "@entity/domain/models/employee/contracts/termination/medical-leave-period.model";
import { ContractTerminationFormValuesValidated } from "@entity/presentation/component/employee-form/contract-termination/form/contract-termination-form";
import { inject, injectable } from "inversify";

@injectable()
export class CreateContractTerminationMapper {
    constructor(
        @inject(TerminationReasonEnumMapper)
        private readonly terminationReasonEnumMapper: TerminationReasonEnumMapper,
        @inject(TerminationCategoryEnumMapper)
        private readonly terminationCategoryEnumMapper: TerminationCategoryEnumMapper,
        @inject(MedicalLeaveSubtypeEnumMapper)
        private readonly medicalLeaveSubtypeEnumMapper: MedicalLeaveSubtypeEnumMapper,
        @inject(MedicalLeavePeriodMapper)
        private readonly medicalLeavePeriodMapper: MedicalLeavePeriodMapper,
    ) {}

    mapToCreateDto(
        createContractTermination: CreateContractTermination,
    ): CreateContractTerminationBody {
        const type = createContractTermination.type
            ? this.terminationReasonEnumMapper.mapToDto(
                  createContractTermination.type,
              )
            : null;

        const terminationCategory = this.terminationCategoryEnumMapper.reverse(
            createContractTermination.terminationCategory,
        );

        const medicalLeaveSubtype =
            createContractTermination.medicalLeaveSubtype
                ? this.medicalLeaveSubtypeEnumMapper.reverse(
                      createContractTermination.medicalLeaveSubtype,
                  )
                : undefined;

        const medicalLeavePeriods =
            createContractTermination.medicalLeavePeriods
                ? createContractTermination.medicalLeavePeriods.map(
                      (period) => {
                          // For create, we don't include the termination ID - backend will set it
                          return {
                              start_date: period.startDate.toISODate() ?? "",
                              end_date: period.endDate.toISODate() ?? "",
                              company_salary_percentage:
                                  period.companySalaryPercentage.toString(),
                              project_imputable_percentage:
                                  period.projectImputablePercentage.toString(),
                              observations: period.observations ?? null,
                          };
                      },
                  )
                : undefined;

        return {
            termination_category: terminationCategory!,
            start_date: createContractTermination.startDate.toISODate() ?? "",
            reason: createContractTermination.reason,
            type: type,
            medical_leave_subtype: medicalLeaveSubtype,
            contract: createContractTermination.contractId,
            cost: createContractTermination.cost?.toString() ?? undefined,
            total_cost:
                createContractTermination.totalCost?.toString() ?? undefined,
            end_date: createContractTermination.endDate?.toISODate() ?? null,
            medical_leave_periods: medicalLeavePeriods,
        };
    }

    mapFromFormValues(
        contractTerminationForm: ContractTerminationFormValuesValidated,
    ): CreateContractTermination {
        const medicalLeavePeriods =
            contractTerminationForm.medicalLeavePeriods?.map(
                (period) =>
                    new CreateMedicalLeavePeriod(
                        0, // Will be set by backend after creating termination
                        period.startDate!,
                        period.endDate!,
                        period.companySalaryPercentage!,
                        period.projectImputablePercentage!,
                        period.observations,
                    ),
            );

        const createContractTermination = new CreateContractTermination(
            contractTerminationForm.terminationCategory,
            contractTerminationForm.startDate,
            contractTerminationForm.reason,
            contractTerminationForm.terminationReason ?? null,
            contractTerminationForm.contractId,
            contractTerminationForm.medicalLeaveSubtype,
            contractTerminationForm.cost,
            contractTerminationForm.totalCost,
            contractTerminationForm.endDate,
            medicalLeavePeriods,
        );

        return createContractTermination;
    }
}
